/**
 * The three task predicates, named side by side because they disagree.
 *
 * ADR 0009 said to **name** them rather than unify them, and #154 is where the divergence was first
 * written down. Three surfaces each ask "is this task live?" and each answers differently; the file
 * exists so the next reader sees all three at once instead of finding the third one months later.
 *
 * | | Excludes `done` | Excludes `someday` | Requires a `dueDate` |
 * | --- | --- | --- | --- |
 * | `isDueTodayCandidate` | yes | **yes** | **yes** |
 * | `isUpcomingCandidate` | yes | no | no |
 * | `isProjectOverdue` | yes | no | yes, and it must be past |
 *
 * Whether the three *should* agree is a product question this file does not answer. It is the
 * restructure's job to place them, not to decide them.
 */

import type { Entry } from '@/document'
import type { TaskMeta } from './meta'

function taskMeta(entry: Entry): TaskMeta | undefined {
  return entry.metadata as TaskMeta | undefined
}

function isLiveTask(entry: Entry): boolean {
  return entry.type === 'task' && !entry.archivedAt
}

/**
 * Today's list. The strictest of the three: a `someday` task is not today's problem, and a task
 * with no due date belongs to Upcoming or Projects rather than here.
 */
export function isDueTodayCandidate(entry: Entry): boolean {
  if (!isLiveTask(entry)) return false
  const meta = taskMeta(entry)
  if (meta?.status === 'done' || meta?.status === 'someday') return false
  return Boolean(meta?.dueDate)
}

/**
 * The upcoming timeline. The loosest: it keeps `someday`, and it keeps undated tasks, which it
 * files under Later as unscheduled.
 */
export function isUpcomingCandidate(entry: Entry): boolean {
  if (!isLiveTask(entry)) return false
  return taskMeta(entry)?.status !== 'done'
}

/**
 * A project's attention count. Keeps `someday` like the timeline, but needs a due date already in
 * the past — `today` is a `YYYY-MM-DD` string, compared lexicographically, which is why
 * `formatDateString` and not `toISOString` is what produces it.
 */
export function isProjectOverdue(entry: Entry, today: string): boolean {
  if (!isLiveTask(entry)) return false
  const meta = taskMeta(entry)
  if (meta?.status === 'done') return false
  return Boolean(meta?.dueDate && meta.dueDate < today)
}
